import { useCallback, useRef, useState } from "react";
import { useLazyQuery } from "@apollo/client";
import {
  CHANGE_REQUEST_STATUS_CHANGE_EMAIL_PREVIEW_QUERY,
  ChangeRequestStatus,
  type ChangeRequestStatusChangeEmailPreview,
  type ChangeRequestStatusChangeEmailPreviewQueryResponse,
  type ChangeRequestStatusChangeEmailPreviewQueryVariables
} from "../queries";

type PreviewableChangeRequestStatus = ChangeRequestStatus.APPROVED | ChangeRequestStatus.REJECTED;

export function useChangeRequestStatusChangeEmailPreview(
  changeRequestId: string,
  status: PreviewableChangeRequestStatus
) {
  const [preview, setPreview] = useState<ChangeRequestStatusChangeEmailPreview | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(false);
  const activeRequestIdRef = useRef(0);

  const [fetchPreview] = useLazyQuery<
    ChangeRequestStatusChangeEmailPreviewQueryResponse,
    ChangeRequestStatusChangeEmailPreviewQueryVariables
  >(CHANGE_REQUEST_STATUS_CHANGE_EMAIL_PREVIEW_QUERY, {
    fetchPolicy: "network-only"
  });

  const resetPreview = useCallback(() => {
    activeRequestIdRef.current += 1;
    setPreview(null);
    setError(null);
    setLoading(false);
  }, []);

  const loadPreview = useCallback(
    async (messageToMember?: string) => {
      const requestId = activeRequestIdRef.current + 1;
      activeRequestIdRef.current = requestId;
      const trimmedMessage = messageToMember?.trim();

      setLoading(true);
      setPreview(null);
      setError(null);

      const result = await fetchPreview({
        variables: {
          changeRequestId,
          status,
          messageToMember: trimmedMessage || null
        }
      });

      if (requestId !== activeRequestIdRef.current) {
        return result;
      }

      if (result.error) {
        setError(result.error);
        setPreview(null);
      } else {
        setPreview(result.data?.changeRequestStatusChangeEmailPreview ?? null);
        setError(null);
      }

      setLoading(false);
      return result;
    },
    [changeRequestId, fetchPreview, status]
  );

  return {
    loadPreview,
    resetPreview,
    preview,
    loading,
    error
  };
}
